/**
 * `SettingsNotifications` — the ONE per-site Notifications surface
 * (founder brief 2026-10-20 "don't we have other kinds of emails and
 * notifications — should be consolidated there as well").
 *
 * Grouped by topic (Orders / Agents / Security) with an Email column
 * and a Slack column of switches per row; descriptions live inside
 * the shared `Tooltip` atom (same `Info size={13}` pattern used by
 * other dashboard surfaces). The Slack column disables with a
 * "Connect Slack →" affordance when the site has no webhook yet, and
 * surfaces a one-line "Connected · Pause all" status + link to
 * Integrations when it does.
 *
 * "Who gets the emails" sits below as one checklist for the site:
 * generalised from the ADR-0245 `security_recipients` map so one
 * table covers every email channel this page can send. Owners /
 * admins may edit; everyone else is read-only.
 *
 * All switches default on (missing JSONB key → `true`). Adding a new
 * channel (future: WhatsApp) is a new column; adding a new event is a
 * new row — no schema change because `site.notification_preferences`
 * is a JSONB bag of booleans and the gate in
 * `services/api/src/threat/security-alerts-v2-gate.ts` iterates its
 * key list.
 */

import { useCallback, useEffect, useState } from 'react';
import { Info } from 'lucide-react';

import type { Site } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { useToast } from '../../app/providers.js';
import {
  type SiteNotificationPrefKey,
  type SiteNotificationRecipient,
  getSiteNotificationPreferences,
  patchSiteNotificationPreferences,
} from '../../api/site-notification-preferences.js';
import { useC } from '../../lib/theme.js';
import { Card, Toggle, Tooltip } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/**
 * One row = one event. Each row has an Email column and (optionally) a
 * Slack column; the Slack column's `key` of `null` renders a muted "–"
 * (used for the Monday agent email, where Slack is not a channel).
 */
interface GroupRow {
  readonly label: string;
  readonly tooltip: string;
  readonly email_key: SiteNotificationPrefKey;
  readonly slack_key: SiteNotificationPrefKey | null;
}

interface Group {
  readonly id: string;
  readonly title: string;
  readonly rows: readonly GroupRow[];
}

const GROUPS: readonly Group[] = [
  {
    id: 'orders',
    title: 'ORDERS',
    rows: [
      {
        label: 'Orders waiting for your approval',
        tooltip:
          'A grouped email + Slack post when an agent order needs a human to '
            + 'review it. One digest per site every ten minutes at most. Covers '
            + 'orders held by any guardrail.',
        email_key: 'escalation_email',
        slack_key: 'escalation_slack',
      },
    ],
  },
  {
    id: 'agents',
    title: 'AGENTS',
    rows: [
      {
        label: 'Monday agent email',
        tooltip:
          'The weekly digest that lands Monday morning: which AI agents '
            + 'visited this site, what they asked for, what your guardrails '
            + 'refused, and anything new worth knowing. Email only — this '
            + 'digest is not sent to Slack.',
        email_key: 'agent_weekly_email',
        slack_key: null,
      },
      {
        label: 'Agent skill changes',
        tooltip:
          'An alert when an agent that visits this site changes its '
            + 'declared skill set. The exact hash lives in your dashboard; the '
            + 'notification just tells you something moved so you can go look.',
        email_key: 'manifest_change_email',
        slack_key: 'manifest_change_slack',
      },
    ],
  },
  {
    id: 'security',
    title: 'SECURITY',
    rows: [
      {
        label: 'An attacker got through',
        tooltip:
          'An email + Slack post within minutes when an attacker gets a real '
            + 'response from a sensitive file, like /.env. At most one '
            + 'notification per attacker a day and five a day per site; after '
            + 'that, one message lists the rest.',
        email_key: 'security_got_through_email',
        slack_key: 'security_got_through_slack',
      },
      {
        label: 'New group of attackers',
        tooltip:
          'An email + Slack post when three or more attackers start probing '
            + 'this site together for the same files. You will not hear about '
            + 'the same group twice.',
        email_key: 'security_new_campaign_email',
        slack_key: 'security_new_campaign_slack',
      },
      {
        label: 'Security section in the Monday email',
        tooltip:
          'Adds a short Security section to the Monday email: attack counts, '
            + 'anything that got through, new groups of attackers and the '
            + 'most-requested paths. Only appears in weeks with attacks.',
        email_key: 'security_weekly_digest',
        slack_key: null,
      },
    ],
  },
];

export interface SettingsNotificationsProps {
  readonly site: Pick<Site, 'site_id' | 'domain'>;
  /**
   * Test-only override for the Slack-connected state. In production
   * the component self-fetches via `api.sites.getSlackWebhookStatus`;
   * tests pass this to avoid wiring the full api context.
   */
  readonly slack_connected_override?: boolean | undefined;
  readonly slack_channel_label_override?: string | undefined;
}

export function SettingsNotifications(
  { site, slack_connected_override, slack_channel_label_override }: SettingsNotificationsProps,
): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const api = useApi();
  const toast = useToast();
  const [prefs, setPrefs] = useState<Record<SiteNotificationPrefKey, boolean> | null>(null);
  const [recipients, setRecipients] = useState<readonly SiteNotificationRecipient[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState<SiteNotificationPrefKey | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Slack-connected state: `undefined` while the status call is in
  // flight, `true` / `false` once resolved. A failed fetch collapses
  // to `false` (treat an unreachable status as "not connected") so a
  // transient origin blip doesn't lock the Slack switches on.
  const [slackConnected, setSlackConnected] = useState<boolean | undefined>(
    slack_connected_override,
  );

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const r = await getSiteNotificationPreferences(http, site.site_id);
      if (cancelled) return;
      if (r.kind === 'ok') {
        setPrefs(r.value.preferences);
        setRecipients(r.value.recipients);
        setCanEdit(r.value.can_edit);
        setLoadError(null);
      } else {
        // The server returns 404 for any of: unknown site_id, a
        // soft-deleted site, or an RLS-filtered row — so a 404 is not
        // uniquely "wrong account" and we must not say so. Keep the
        // message plain and point the user at the switcher.
        setLoadError(
          r.kind === 'not_found'
            ? 'Could not load notification preferences for this site. Pick another site from the switcher.'
            : r.kind === 'forbidden'
              ? 'You do not have access to this site.'
              : r.kind === 'bad_request'
                ? r.message
                : 'Could not load notification preferences.',
        );
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [http, site.site_id]);

  useEffect(() => {
    // The override wins when provided (tests). Only the real path
    // fetches Slack status; this effect is idempotent on re-mount.
    if (slack_connected_override !== undefined) {
      setSlackConnected(slack_connected_override);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const status = await api.sites.getSlackWebhookStatus(
          site.site_id as unknown as Parameters<typeof api.sites.getSlackWebhookStatus>[0],
        );
        if (cancelled) return;
        setSlackConnected(status.configured);
      } catch {
        if (!cancelled) setSlackConnected(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, site.site_id, slack_connected_override]);

  const toggle = useCallback(
    async (key: SiteNotificationPrefKey): Promise<void> => {
      if (!canEdit || prefs === null || pendingKey !== null || http === null) return;
      const previous = prefs[key];
      const next = !previous;
      setPendingKey(key);
      setPrefs({ ...prefs, [key]: next });
      const r = await patchSiteNotificationPreferences(http, site.site_id, {
        [key]: next,
      });
      setPendingKey(null);
      if (r.kind === 'ok') {
        setPrefs(r.value.preferences);
        setRecipients(r.value.recipients);
        toast({
          tone: 'lime',
          title: next ? 'Turned on.' : 'Turned off.',
        });
      } else {
        setPrefs({ ...prefs, [key]: previous });
        toast({
          tone: 'coral',
          title: 'Could not save preference.',
        });
      }
    },
    [http, canEdit, pendingKey, prefs, site.site_id, toast],
  );

  const toggleRecipient = useCallback(
    async (user_id: string): Promise<void> => {
      if (!canEdit || http === null || pendingUserId !== null) return;
      const current = recipients.find((r) => r.user_id === user_id);
      if (current === undefined) return;
      const next = !current.receives;
      setPendingUserId(user_id);
      setRecipients((prev) =>
        prev.map((r) => (r.user_id === user_id ? { ...r, receives: next } : r)),
      );
      const r = await patchSiteNotificationPreferences(http, site.site_id, {
        security_recipients: { [user_id]: next },
      });
      setPendingUserId(null);
      if (r.kind === 'ok') {
        setRecipients(r.value.recipients);
        toast({
          tone: 'lime',
          title: next
            ? `${current.name || current.email} will receive these emails.`
            : `${current.name || current.email} will no longer receive these emails.`,
        });
      } else {
        setRecipients((prev) =>
          prev.map((r2) => (r2.user_id === user_id ? { ...r2, receives: !next } : r2)),
        );
        toast({ tone: 'coral', title: 'Could not save recipient choice.' });
      }
    },
    [http, canEdit, pendingUserId, recipients, site.site_id, toast],
  );

  return (
    <section id="notifications">
      <Card className="p-5 sm:p-6">
        <div
          className="mb-5 pb-4"
          style={{ borderBottom: `1px solid ${C.border}` }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            NOTIFICATIONS
          </div>
          <div
            style={{
              fontFamily: serifFont,
              fontSize: 22,
              color: C.text,
              letterSpacing: -0.5,
              marginBottom: 4,
            }}
          >
            Notifications for {site.domain}
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.5,
            }}
          >
            Pick which emails and Slack posts this site sends. Everything
            starts on. Owners and admins can also pick who gets the emails.
          </div>
        </div>

        {loading ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
            }}
          >
            Loading preferences…
          </div>
        ) : loadError !== null ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
            }}
          >
            {loadError}
          </div>
        ) : prefs !== null ? (
          <div className="space-y-4">
            <SlackLine
              C={C}
              connected={slackConnected}
              channel_label={slack_channel_label_override}
            />
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
              }}
              data-testid="slack-visibility-note"
            >
              Slack messages show the full address to everyone in the channel.
            </div>
            {/* Column headers once at the top; each group then reuses them. */}
            <div
              className="grid gap-2 items-end"
              style={{ gridTemplateColumns: '1fr 60px 60px' }}
              data-testid="notifications-column-headers"
            >
              <span />
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  letterSpacing: 1.5,
                  textAlign: 'center',
                }}
              >
                EMAIL
              </span>
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  letterSpacing: 1.5,
                  textAlign: 'center',
                }}
              >
                SLACK
              </span>
            </div>
            {GROUPS.map((g) => (
              <GroupBlock
                key={g.id}
                C={C}
                group={g}
                prefs={prefs}
                pendingKey={pendingKey}
                canEdit={canEdit}
                slack_connected={slackConnected}
                onToggle={toggle}
              />
            ))}

            <RecipientsBlock
              C={C}
              recipients={recipients}
              canEdit={canEdit}
              pendingUserId={pendingUserId}
              onToggle={toggleRecipient}
            />

            {!canEdit ? (
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.muted,
                  marginTop: 8,
                }}
              >
                You can view these preferences. Owners and admins can
                change them.
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>
    </section>
  );
}

/* -------------------------------------------------------------------------
 * Slack connected / disconnected line (shared with the Slack card)
 * ----------------------------------------------------------------------- */

interface SlackLineProps {
  readonly C: ReturnType<typeof useC>;
  readonly connected: boolean | undefined;
  readonly channel_label: string | undefined;
}

function SlackLine({ C, connected, channel_label }: SlackLineProps): JSX.Element {
  if (connected === undefined) {
    return (
      <div
        data-testid="slack-line-loading"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
        }}
      >
        Slack: Checking…
      </div>
    );
  }
  if (!connected) {
    return (
      <div
        data-testid="slack-line-disconnected"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
        }}
      >
        Slack: Not connected.{' '}
        <a
          href="?view=settings&settings_tab=integrations&settings_section=slack"
          data-testid="slack-connect-link"
          style={{ color: C.text, textDecoration: 'underline' }}
        >
          Connect Slack →
        </a>
      </div>
    );
  }
  return (
    <div
      data-testid="slack-line-connected"
      style={{
        fontFamily: sansFont,
        fontSize: 12,
        color: C.muted,
      }}
    >
      Slack: {channel_label !== undefined && channel_label.length > 0
        ? `${channel_label} · `
        : ''}Connected ·{' '}
      <a
        href="?view=settings&settings_tab=integrations&settings_section=slack"
        data-testid="slack-pause-all-link"
        style={{ color: C.text, textDecoration: 'underline' }}
      >
        Pause all
      </a>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Group block — three columns (label + Email + Slack switches)
 * ----------------------------------------------------------------------- */

interface GroupBlockProps {
  readonly C: ReturnType<typeof useC>;
  readonly group: Group;
  readonly prefs: Record<SiteNotificationPrefKey, boolean>;
  readonly pendingKey: SiteNotificationPrefKey | null;
  readonly canEdit: boolean;
  readonly slack_connected: boolean | undefined;
  readonly onToggle: (key: SiteNotificationPrefKey) => void;
}

function GroupBlock(props: GroupBlockProps): JSX.Element {
  const { C, group, prefs, pendingKey, canEdit, slack_connected, onToggle } = props;
  return (
    <div
      data-testid={`notifications-group-${group.id}`}
      className="p-3"
      style={{ border: `1px solid ${C.border}`, borderRadius: 2 }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 10,
        }}
      >
        {group.title}
      </div>
      <div className="space-y-2">
        {group.rows.map((row) => (
          <div
            key={row.email_key}
            className="grid gap-2 items-center"
            style={{ gridTemplateColumns: '1fr 60px 60px' }}
          >
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span>{row.label}</span>
              <Tooltip content={row.tooltip} side="top">
                <span
                  aria-label={`About ${row.label}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: C.muted,
                    cursor: 'help',
                  }}
                >
                  <Info size={13} />
                </span>
              </Tooltip>
            </div>
            <ChannelSwitch
              row_label={row.label}
              channel="email"
              value={prefs[row.email_key]}
              disabled={!canEdit || pendingKey !== null}
              onClick={() => onToggle(row.email_key)}
            />
            {row.slack_key === null ? (
              <div
                aria-hidden="true"
                data-testid={`slack-cell-na-${row.email_key}`}
                style={{
                  fontFamily: sansFont,
                  fontSize: 14,
                  color: C.muted,
                  textAlign: 'center',
                }}
              >
                –
              </div>
            ) : slack_connected === false ? (
              <div
                data-testid={`slack-cell-connect-${row.slack_key}`}
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  letterSpacing: 1,
                  color: C.muted,
                  textAlign: 'center',
                }}
              >
                —
              </div>
            ) : (
              <ChannelSwitch
                row_label={row.label}
                channel="slack"
                value={prefs[row.slack_key]}
                disabled={!canEdit || pendingKey !== null}
                onClick={() => onToggle(row.slack_key as SiteNotificationPrefKey)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface ChannelSwitchProps {
  readonly row_label: string;
  readonly channel: 'email' | 'slack';
  readonly value: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
}

/**
 * Founder bug 2026-10-20 Report 2 — the Notifications page shipped
 * with a bespoke 44×26 switch that read as bulky next to the compact
 * 36×20 `Toggle` atom used in SettingsSkills / AutoRefundSection /
 * every other Settings surface. Switched to the shared `Toggle` so
 * the Notifications grid matches the rest of Settings. The outer
 * `justify-self: center` is applied via wrapper so the atom stays
 * untouched (small, animated, lime-keyed).
 */
function ChannelSwitch(props: ChannelSwitchProps): JSX.Element {
  const { row_label, channel, value, disabled, onClick } = props;
  // The parent already knows which key to flip; the atom's `(next: boolean) => void`
  // callback is deliberately ignored here (the key encodes the direction).
  const handleChange = (_next: boolean): void => { onClick(); };
  return (
    <div
      data-testid={`notifications-channel-${channel}`}
      style={{ justifySelf: 'center' }}
    >
      <Toggle
        on={value}
        onChange={handleChange}
        disabled={disabled}
        aria-label={`${row_label} ${channel}`}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Recipients block (generalised from the ADR-0245 security_recipients map)
 * ----------------------------------------------------------------------- */

interface RecipientsBlockProps {
  readonly C: ReturnType<typeof useC>;
  readonly recipients: readonly SiteNotificationRecipient[];
  readonly canEdit: boolean;
  readonly pendingUserId: string | null;
  readonly onToggle: (user_id: string) => void;
}

function RecipientsBlock(props: RecipientsBlockProps): JSX.Element | null {
  const { C, recipients, canEdit, pendingUserId, onToggle } = props;
  if (recipients.length === 0) return null;
  return (
    <div
      data-testid="site-notification-recipients"
      style={{
        marginTop: 20,
        paddingTop: 16,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span>WHO GETS THE EMAILS</span>
        <Tooltip
          content={
            'This list is for email. Slack posts always go to the connected '
              + 'channel. Owners and admins receive by default; reviewers receive '
              + 'approval-queue emails too. Tick anyone else who should.'
          }
          side="top"
        >
          <span
            aria-label="About recipients"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: C.muted,
              cursor: 'help',
            }}
          >
            <Info size={13} />
          </span>
        </Tooltip>
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          marginBottom: 10,
          lineHeight: 1.5,
        }}
      >
        One list for every email channel. Defaults by role; tick to add,
        untick to remove.
      </div>
      <div className="space-y-2">
        {recipients.map((r) => {
          const row_pending = pendingUserId === r.user_id;
          const row_disabled = !canEdit || row_pending;
          const display_name = r.name && r.name.length > 0 ? r.name : r.email;
          return (
            <div
              key={r.user_id}
              className="flex items-center justify-between gap-3 p-2"
              style={{ border: `1px solid ${C.border}`, borderRadius: 2 }}
            >
              <div className="flex-1 min-w-0">
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.text,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {display_name}
                </div>
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.muted,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  {r.role}
                  {r.name && r.name.length > 0 && r.email !== r.name
                    ? ` · ${r.email}`
                    : ''}
                </div>
              </div>
              <button
                type="button"
                role="checkbox"
                aria-checked={r.receives}
                aria-label={`Emails to ${display_name}`}
                disabled={row_disabled}
                onClick={() => { onToggle(r.user_id); }}
                style={{
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  borderRadius: 2,
                  border: `1px solid ${r.receives ? C.lime : C.border}`,
                  background: r.receives ? C.lime : C.surface2,
                  cursor: row_disabled ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  color: r.receives ? C.ink : 'transparent',
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {r.receives ? '✓' : ''}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

